import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Download, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export const PWAInstallPrompt: React.FC = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show the install prompt
      setShowInstallPrompt(true);
    };

    const handleAppInstalled = () => {
      // Hide the app-provided install promotion
      setShowInstallPrompt(false);
      // Clear the deferredPrompt so it can be garbage collected
      setDeferredPrompt(null);
      // Optionally, send analytics event to track successful installs
      console.log('PWA was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    setDeferredPrompt(null);
  };

  if (!showInstallPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4">
      <Card className="w-80 shadow-lg border-gebeya-pink/20 bg-white backdrop-blur-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold text-gebeya-purple">
              {t('pwa.installPrompt.title')}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <CardDescription className="text-xs text-gray-600 mb-3">
            {t('pwa.installPrompt.description')}
          </CardDescription>
          <div className="flex gap-2">
            <Button
              onClick={handleInstallClick}
              className="flex-1 bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white text-xs font-medium"
              size="sm"
            >
              <Download className="h-3 w-3 mr-1" />
              {t('pwa.installPrompt.installButton')}
            </Button>
            <Button
              variant="outline"
              onClick={handleDismiss}
              className="text-xs border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
              size="sm"
            >
              {t('pwa.installPrompt.notNowButton')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Hook to check if PWA is installable
export const usePWAInstallable = () => {
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    const checkInstallable = () => {
      // Check if the app is already installed
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isInStandaloneMode = window.navigator.standalone === true;
      
      if (isStandalone || isInStandaloneMode) {
        setIsInstallable(false);
        return;
      }

      // Check if the app meets the installability criteria
      // This is a simplified check - in reality, the browser decides
      setIsInstallable(true);
    };

    checkInstallable();
    window.addEventListener('appinstalled', () => setIsInstallable(false));

    return () => {
      window.removeEventListener('appinstalled', () => setIsInstallable(false));
    };
  }, []);

  return isInstallable;
}; 